import { useEffect, useRef } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { cloudSyncEnabled } from "../config";
import { usePreferences } from "../preferences/PreferencesProvider";
import styles from "./Layout.module.css";

const LINKS = [
  { to: "/", label: "Inicio", end: true },
  { to: "/search", label: "Buscar", end: false },
  { to: "/library", label: "Mi lista", end: false },
  { to: "/settings", label: "Ajustes", end: false },
];

export function Layout() {
  const { state } = useAuth();
  const { prefs, store } = usePreferences();
  const navigate = useNavigate();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const firstRender = useRef(true);

  // Atajo global "/" o Ctrl+K para ir al buscador desde cualquier pantalla (salvo escribiendo en un campo).
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isEditable = target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT" || target.isContentEditable);
      if (!isEditable && event.key === "/" && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        navigate("/search");
      } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        navigate("/search");
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [navigate]);

  // Al cambiar de ruta se desplaza al inicio para mantener continuidad en navegacion SPA.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (typeof navigator === "undefined" || !navigator.userAgent.includes("jsdom")) {
      window.scrollTo?.({ top: 0 });
    }
  }, [location.pathname]);

  /**
   * Conmutador de dos pasos siguiendo `dark-mode-toggle`:
   * invierte el esquema actualmente renderizado y elimina el override cuando coincide con el sistema.
   */
  const toggleThemeQuick = () => {
    const systemDark = typeof window.matchMedia === "function" ? window.matchMedia("(prefers-color-scheme: dark)").matches : true;
    const currentlyDark = prefs.theme === "dark" || (prefs.theme === "system" && systemDark);
    const target = currentlyDark ? "light" : "dark";
    const systemScheme = systemDark ? "dark" : "light";
    store.update({ theme: target === systemScheme ? "system" : target });
  };

  return (
    <>
      <a href="#main-content" className="skip-link">
        Saltar al contenido principal
      </a>
      <header className={styles.header}>
        <nav className={styles.nav} aria-label="Principal">
          <NavLink to="/" className={styles.brand}>
            <img src="/logo.png" alt="WebinAnime" width={32} height={32} className={styles.brandLogo} />
            <span>Webin</span>Anime <span className={styles.proBadge}>PRO</span>
          </NavLink>
          <div className={styles.links}>
            {LINKS.map((link) => (
              <NavLink key={link.to} to={link.to} end={link.end} className={({ isActive }) => (isActive ? styles.active : undefined)}>
                {link.label}
              </NavLink>
            ))}
            {cloudSyncEnabled && (
              <NavLink to="/account" className={({ isActive }) => (isActive ? styles.active : undefined)}>
                {state.status === "signedIn" ? "Cuenta" : "Entrar"}
              </NavLink>
            )}
            <button
              type="button"
              className={styles.themeBtn}
              onClick={toggleThemeQuick}
              aria-label={`Cambiar tema (actual: ${prefs.theme === "system" ? "sistema" : prefs.theme === "dark" ? "oscuro" : "claro"})`}
            >
              {prefs.theme === "light" ? "☀" : "☾"}
            </button>
          </div>
        </nav>
      </header>
      <main id="main-content" ref={mainRef} tabIndex={-1} className={styles.main}>
        <Outlet />
      </main>
      <nav className={styles.bottomNav} aria-label="Navegación móvil">
        {LINKS.map((link) => (
          <NavLink key={link.to} to={link.to} end={link.end} className={({ isActive }) => (isActive ? `${styles.bottomLink} ${styles.bottomActive}` : styles.bottomLink)}>
            <span>{link.label}</span>
          </NavLink>
        ))}
      </nav>
    </>
  );
}
